import { useRef, useEffect } from "react";
import { Bold, Italic, Underline, List, ListOrdered, Heading2, AlignLeft, AlignCenter, Undo2, Redo2 } from "lucide-react";

const Btn = ({ onClick, title, children, testid }) => (
  <button type="button" data-testid={testid} title={title} onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    className="h-8 w-8 grid place-items-center rounded-md hover:bg-accent text-foreground/80">{children}</button>
);

export function RichTextEditor({ value, onChange, readOnly = false }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value) ref.current.innerHTML = value || "";
  }, [value]);
  const cmd = (c, arg) => { document.execCommand(c, false, arg); onChange(ref.current.innerHTML); };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="rich-editor">
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-border bg-muted/40">
          <Btn testid="rte-bold" title="Bold" onClick={() => cmd("bold")}><Bold className="w-4 h-4" /></Btn>
          <Btn testid="rte-italic" title="Italic" onClick={() => cmd("italic")}><Italic className="w-4 h-4" /></Btn>
          <Btn testid="rte-underline" title="Underline" onClick={() => cmd("underline")}><Underline className="w-4 h-4" /></Btn>
          <span className="w-px h-5 bg-border mx-1" />
          <Btn testid="rte-h2" title="Heading" onClick={() => cmd("formatBlock", "H2")}><Heading2 className="w-4 h-4" /></Btn>
          <Btn testid="rte-p" title="Paragraph" onClick={() => cmd("formatBlock", "P")}><AlignLeft className="w-4 h-4" /></Btn>
          <Btn testid="rte-ul" title="Bullet list" onClick={() => cmd("insertUnorderedList")}><List className="w-4 h-4" /></Btn>
          <Btn testid="rte-ol" title="Numbered list" onClick={() => cmd("insertOrderedList")}><ListOrdered className="w-4 h-4" /></Btn>
          <Btn testid="rte-center" title="Center" onClick={() => cmd("justifyCenter")}><AlignCenter className="w-4 h-4" /></Btn>
          <span className="w-px h-5 bg-border mx-1" />
          <Btn testid="rte-undo" title="Undo" onClick={() => cmd("undo")}><Undo2 className="w-4 h-4" /></Btn>
          <Btn testid="rte-redo" title="Redo" onClick={() => cmd("redo")}><Redo2 className="w-4 h-4" /></Btn>
        </div>
      )}
      <div ref={ref} data-testid="rte-content" contentEditable={!readOnly} suppressContentEditableWarning
        onInput={() => onChange(ref.current.innerHTML)}
        className="prose prose-sm dark:prose-invert max-w-none min-h-[480px] p-6 sm:p-10 outline-none bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mt-4" />
    </div>
  );
}
